// Versions.tsx — version timeline + side-by-side diff viewer (live API).

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useToast } from "@/components/primitives/Toast";
import { api } from "@/lib/api";
import type { RecordVersion } from "@/lib/types";

type ChangeKind = "added" | "removed" | "modified" | "unchanged";

function asStringMap(d: Record<string, unknown> = {}): Record<string, string> {
  const o: Record<string, string> = {};
  for (const [k, v] of Object.entries(d)) o[k] = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
  return o;
}

function computeDiff(a: Record<string, string>, b: Record<string, string>):
  Array<{ key: string; aVal?: string; bVal?: string; kind: ChangeKind }>
{
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).sort();
  return keys.map(k => {
    const aVal = a[k], bVal = b[k];
    let kind: ChangeKind;
    if (aVal == null && bVal != null) kind = "added";
    else if (aVal != null && bVal == null) kind = "removed";
    else if (aVal !== bVal) kind = "modified";
    else kind = "unchanged";
    return { key: k, aVal, bVal, kind };
  });
}

export function Versions() {
  const { domain = "instruments", recordId = "" } = useParams();
  const [versions, setVersions] = useState<RecordVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vA, setVA] = useState<number | null>(null);
  const [vB, setVB] = useState<number | null>(null);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const toast = useToast();

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await api.versions.list(domain, recordId);
      const sorted = [...list].sort((a, b) => b.version - a.version);
      setVersions(sorted);
      if (sorted.length) {
        setVB(sorted[0].version);
        setVA(sorted[1]?.version ?? sorted[0].version);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load versions");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!recordId) { setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, recordId]);

  const A = useMemo(() => versions.find(v => v.version === vA), [versions, vA]);
  const B = useMemo(() => versions.find(v => v.version === vB), [versions, vB]);

  const onRestore = async () => {
    if (vA == null) return;
    setRestoring(true);
    try {
      await api.versions.restore(domain, recordId, vA);
      toast(`Restored v${vA}`, "ok");
      await load();
    } catch (e) {
      toast(e instanceof Error ? `Restore failed: ${e.message}` : "Restore failed", "error");
    } finally {
      setRestoring(false);
    }
  };

  if (!recordId) {
    return <div style={{ padding: "28px 40px" }}>No record selected. Open from a record detail page.</div>;
  }
  if (loading) {
    return <div style={{ padding: "28px 40px", color: "var(--md-on-surface-var)" }}>Loading versions…</div>;
  }
  if (error) {
    return <div style={{ padding: "28px 40px", color: "var(--md-error)" }}>{error}</div>;
  }
  if (!versions.length || !A || !B) {
    return (
      <div style={{ padding: "28px 40px" }}>
        <div className="font-mono" style={eyebrowStyle()}>
          <Link to={`/browse/${domain}`} style={{ color: "inherit", textDecoration: "none" }}>{domain.toUpperCase()}</Link>
          {" · "}<Link to={`/browse/${domain}/${recordId}`} style={{ color: "inherit", textDecoration: "none" }}>{recordId}</Link>
          {" · "}<span style={{ color: "var(--md-on-surface)" }}>VERSION HISTORY</span>
        </div>
        <h1 className="font-display" style={{ font: "var(--t-display-sm)" }}>No version history</h1>
        <p style={{ color: "var(--md-on-surface-var)" }}>This record has no recorded versions yet.</p>
      </div>
    );
  }

  const aMap = asStringMap(A.data);
  const bMap = asStringMap(B.data);
  const diff = computeDiff(aMap, bMap);
  const visible = showUnchanged ? diff : diff.filter(d => d.kind !== "unchanged");
  const changed = diff.filter(d => d.kind !== "unchanged").length;

  return (
    <div style={{ padding: "28px 40px 80px", maxWidth: 1400, margin: "0 auto" }}>
      <div className="font-mono" style={eyebrowStyle()}>
        <Link to={`/browse/${domain}`} style={{ color: "inherit", textDecoration: "none" }}>{domain.toUpperCase()}</Link>
        {" · "}
        <Link to={`/browse/${domain}/${recordId}`} style={{ color: "inherit", textDecoration: "none" }}>{recordId}</Link>
        {" · "}<span style={{ color: "var(--md-on-surface)" }}>VERSION HISTORY</span>
      </div>
      <h1 className="font-display" style={{
        margin: "8px 0 24px", font: "var(--t-display-sm)", letterSpacing: "-0.025em",
      }}>
        <span style={{ fontStyle: "italic", fontWeight: 400 }}>{versions.length}</span> versions on record
      </h1>

      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 24, alignItems: "start" }}>
        <aside aria-label="Version timeline" style={{
          background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)",
          borderRadius: "var(--r-lg)", padding: "16px 0", position: "sticky", top: 80,
        }}>
          <div style={{ padding: "0 20px 12px", borderBottom: "1px solid var(--md-outline-var)" }}>
            <SectionEyebrow>Timeline</SectionEyebrow>
          </div>
          <ol style={{ listStyle: "none", padding: 0, margin: 0, position: "relative" }}>
            <div aria-hidden style={{
              position: "absolute", left: 27, top: 12, bottom: 12,
              width: 2, background: "var(--md-outline-var)",
            }} />
            {versions.map((v, i) => {
              const selectedA = v.version === vA, selectedB = v.version === vB;
              return (
                <li key={v.version} style={{ position: "relative" }}>
                  <button
                    onClick={() => setVB(v.version)}
                    onDoubleClick={() => setVA(v.version)}
                    className="row-hover"
                    aria-current={selectedB}
                    style={{
                      width: "100%", textAlign: "left", appearance: "none", border: "none",
                      background: selectedB ? "var(--md-secondary-container)" : "transparent",
                      color: "inherit", cursor: "pointer",
                      padding: "12px 20px 12px 56px",
                      display: "grid", gap: 4,
                    }}
                  >
                    <span aria-hidden style={{
                      position: "absolute", left: 21, top: 18,
                      width: 14, height: 14, borderRadius: 999,
                      background: i === 0 ? "var(--md-primary)" : selectedA ? "var(--md-tertiary)" : "var(--md-sc-high)",
                      border: "3px solid var(--md-sc-lowest)",
                      boxShadow: i === 0 ? "0 0 0 3px var(--md-primary-container)" : "none",
                    }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="font-mono" style={{ font: "var(--t-mono-md)", fontWeight: 600 }}>v{v.version}</span>
                      {i === 0 && <Pill tone="primary">current</Pill>}
                      {selectedA && <Pill tone="tertiary">A</Pill>}
                      {selectedB && !selectedA && <Pill tone="secondary">B</Pill>}
                    </div>
                    <div style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>
                      {v.changedBy} — <span className="font-mono">{v.changedAt.slice(0, 10)}</span>
                    </div>
                    <div style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface)" }}>{v.comment ?? "—"}</div>
                  </button>
                </li>
              );
            })}
          </ol>
          <div style={{ padding: "12px 20px 4px", font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>
            Click to pick B (current). Double-click to pick A (compare against).
          </div>
        </aside>

        <section aria-label="Diff" style={{ minWidth: 0 }}>
          <div style={{
            display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap",
          }}>
            <label style={{ font: "var(--t-label-md)", display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Compare</span>
              <select value={vA ?? ""} onChange={e => setVA(Number(e.target.value))} style={selectStyle()}>
                {versions.map(v => <option key={v.version} value={v.version}>v{v.version} · {v.changedBy}</option>)}
              </select>
            </label>
            <div style={{ font: "var(--t-headline-sm)", color: "var(--md-on-surface-var)", paddingTop: 18 }}>→</div>
            <label style={{ font: "var(--t-label-md)", display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)", textTransform: "uppercase", letterSpacing: "0.08em" }}>With</span>
              <select value={vB ?? ""} onChange={e => setVB(Number(e.target.value))} style={selectStyle()}>
                {versions.map(v => <option key={v.version} value={v.version}>v{v.version} · {v.changedBy}</option>)}
              </select>
            </label>

            <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
              <label style={{ display: "inline-flex", gap: 6, alignItems: "center", font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>
                <input type="checkbox" checked={showUnchanged} onChange={e => setShowUnchanged(e.target.checked)} />
                Show unchanged
              </label>
              <button style={btnOutlined()} onClick={onRestore} disabled={restoring || vA == null}>{restoring ? "Restoring…" : `Restore v${vA}`}</button>
            </div>
          </div>

          <div role="status" aria-live="polite" style={{
            font: "var(--t-body-sm)", color: "var(--md-on-surface-var)", marginBottom: 12,
          }}>
            <span className="font-mono">{changed}</span> field{changed === 1 ? "" : "s"} changed between v{vA} and v{vB}.
          </div>

          {visible.length === 0 ? (
            <div style={{
              padding: 40, textAlign: "center",
              background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)",
              borderRadius: "var(--r-lg)", color: "var(--md-on-surface-var)",
            }}>
              <div style={{ font: "var(--t-title-md)", marginTop: 8 }}>No differences</div>
              <div style={{ font: "var(--t-body-md)", marginTop: 4 }}>v{vA} and v{vB} have identical visible field values.</div>
            </div>
          ) : (
            <div style={{
              border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-lg)",
              background: "var(--md-sc-lowest)", overflow: "hidden",
            }}>
              <div style={{
                display: "grid", gridTemplateColumns: "180px 1fr 1fr", padding: "12px 20px",
                background: "var(--md-sc-low)", font: "var(--t-label-md)",
                color: "var(--md-on-surface-var)", textTransform: "uppercase", letterSpacing: "0.08em",
              }}>
                <div>Field</div>
                <div>v{vA}</div>
                <div>v{vB}</div>
              </div>
              {visible.map((d, i) => <DiffRow key={d.key} row={d} first={i === 0} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function DiffRow({ row, first }: { row: { key: string; aVal?: string; bVal?: string; kind: ChangeKind }; first: boolean }) {
  const styles: Record<ChangeKind, { a: CSSProperties; b: CSSProperties; label: string }> = {
    added: {
      a: { color: "var(--md-on-surface-var)", fontStyle: "italic" },
      b: { background: "var(--md-secondary-container)", color: "var(--md-on-secondary-container)" },
      label: "added",
    },
    removed: {
      a: { background: "var(--md-error-container)", color: "var(--md-on-error-container)", textDecoration: "line-through" },
      b: { color: "var(--md-on-surface-var)", fontStyle: "italic" },
      label: "removed",
    },
    modified: {
      a: { background: "var(--md-error-container)", color: "var(--md-on-error-container)", textDecoration: "line-through" },
      b: { background: "var(--md-tertiary-container)", color: "var(--md-on-tertiary-container)" },
      label: "modified",
    },
    unchanged: { a: {}, b: {}, label: "unchanged" },
  };
  const s = styles[row.kind];
  return (
    <div role="group" aria-label={`${row.kind} change for ${row.key}`} style={{
      display: "grid", gridTemplateColumns: "180px 1fr 1fr",
      padding: "12px 20px", gap: 16, alignItems: "center",
      borderTop: !first ? "1px solid var(--md-outline-var)" : "none",
    }}>
      <div>
        <div className="font-mono" style={{ font: "var(--t-mono-md)" }}>{row.key}</div>
        <div className="font-mono" style={{
          font: "var(--t-mono-sm)", color: tagColor(row.kind), marginTop: 4,
          textTransform: "uppercase", letterSpacing: "0.08em",
        }}>{s.label}</div>
      </div>
      <div className="font-mono" style={{ font: "var(--t-mono-md)", padding: "8px 12px", borderRadius: "var(--r-sm)", ...s.a }}>
        {row.aVal ?? "—"}
      </div>
      <div className="font-mono" style={{ font: "var(--t-mono-md)", padding: "8px 12px", borderRadius: "var(--r-sm)", ...s.b }}>
        {row.bVal ?? "—"}
      </div>
    </div>
  );
}

function tagColor(kind: ChangeKind) {
  if (kind === "added") return "var(--success)";
  if (kind === "removed") return "var(--danger)";
  if (kind === "modified") return "var(--warning)";
  return "var(--md-on-surface-var)";
}

function Pill({ children, tone }: { children: ReactNode; tone: "primary" | "secondary" | "tertiary" }) {
  const bg = tone === "primary" ? "var(--md-primary-container)" : tone === "secondary" ? "var(--md-secondary-container)" : "var(--md-tertiary-container)";
  const fg = tone === "primary" ? "var(--md-on-primary-container)" : tone === "secondary" ? "var(--md-on-secondary-container)" : "var(--md-on-tertiary-container)";
  return <span style={{
    display: "inline-flex", alignItems: "center", padding: "2px 10px",
    borderRadius: "var(--r-pill)", background: bg, color: fg,
    font: "var(--t-label-sm)", letterSpacing: "0.04em",
  }}>{children}</span>;
}

function SectionEyebrow({ children }: { children: ReactNode }) {
  return <div className="font-mono" style={{
    font: "var(--t-label-md)", color: "var(--md-on-surface-var)",
    textTransform: "uppercase", letterSpacing: "0.12em",
  }}>{children}</div>;
}

function eyebrowStyle(): CSSProperties {
  return {
    font: "var(--t-mono-sm)", color: "var(--md-tertiary)",
    textTransform: "uppercase", letterSpacing: "0.12em",
  };
}
function btnOutlined(): CSSProperties {
  return {
    appearance: "none", cursor: "pointer",
    background: "transparent", color: "var(--md-on-surface)",
    border: "1px solid var(--md-outline)",
    font: "var(--t-label-lg)", height: 40, padding: "0 18px",
    borderRadius: "var(--r-pill)",
  };
}
function selectStyle(): CSSProperties {
  return {
    height: 40, padding: "0 14px",
    background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)",
    borderRadius: "var(--r-pill)", font: "var(--t-body-md)",
    color: "var(--md-on-surface)", outline: "none",
  };
}

export default Versions;
